import { FC, useContext, useMemo, useState } from 'react';
import { Table, Button, Checkbox, Input, Typography, theme } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { ArTableType, ArColumnDefinition } from '#types/System.Artifacts.types';
import type { EditorUIElement, UIArTable, UIArTableColumnOverride } from '#types/System.types';
import { LayoutEditorContext } from '../../providers/editor/LayoutEditorContext';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import { columnConfigsMatch, normalizeColumnConfig } from './columnConfigNormalization';
import FlexCol from '../atoms/FlexCol';
import FlexBox from '../atoms/FlexBox';

const { Text } = Typography;

interface ColumnConfigEditorProps {
    element: EditorUIElement;
    tableType: ArTableType;
}

type ColumnRow = {
    typeId: string;
    label: string;
    included: boolean;
    labelOverride: string;
};

const SortableRow: FC<{ id: string; children: React.ReactNode }> = ({ id, children, ...props }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} {...props}>
            <td style={{ width: 40, cursor: 'grab' }} {...attributes} {...listeners}>
                <HolderOutlined />
            </td>
            {children}
        </tr>
    );
};

const resolveColumnLabel = (
    col: ArColumnDefinition,
    fieldTypes: Record<string, { description?: { shortLabel?: string } }>
): string =>
    col.descriptionOverride?.shortLabel
    ?? col.cacheDescription
    ?? fieldTypes[col.typeId]?.description?.shortLabel
    ?? col.typeId;

const ColumnConfigEditor: FC<ColumnConfigEditorProps> = ({ element, tableType }) => {
    const { token } = theme.useToken();
    const editor = useContext(LayoutEditorContext);
    if (!editor) throw new Error('Editor must be used within LayoutEditorProvider');
    const { fieldTypes } = useContext(FieldTypeContext);

    const tableElement = element as UIArTable & { dndId: string };
    const currentConfig = tableElement.columnConfig;
    const tableColumnTypeIds = useMemo(
        () => new Set(tableType.columns.map(col => col.typeId)),
        [tableType.columns]
    );

    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const rows: ColumnRow[] = useMemo(() => {
        const allColumns = tableType.columns;
        const labelsByTypeId = new Map(
            allColumns.map(col => [col.typeId, resolveColumnLabel(col, fieldTypes)])
        );

        if (!currentConfig) {
            return allColumns.map(col => ({
                typeId: col.typeId,
                label: labelsByTypeId.get(col.typeId) ?? col.typeId,
                included: false,
                labelOverride: '',
            }));
        }

        const included: ColumnRow[] = [];
        const includedTypeIds = new Set<string>();

        for (const config of currentConfig) {
            if (includedTypeIds.has(config.typeId)) continue;
            if (!labelsByTypeId.has(config.typeId)) continue;

            includedTypeIds.add(config.typeId);
            included.push({
                typeId: config.typeId,
                label: labelsByTypeId.get(config.typeId) ?? config.typeId,
                included: true,
                labelOverride: config.labelOverride ?? '',
            });
        }

        const excluded: ColumnRow[] = allColumns
            .filter(col => !includedTypeIds.has(col.typeId))
            .map(col => ({
                typeId: col.typeId,
                label: labelsByTypeId.get(col.typeId) ?? col.typeId,
                included: false,
                labelOverride: '',
            }));

        return [...included, ...excluded];
    }, [tableType.columns, currentConfig, fieldTypes]);

    const dispatchConfig = (nextConfig: UIArTableColumnOverride[] | undefined) => {
        const normalizedConfig = normalizeColumnConfig(nextConfig, tableColumnTypeIds);
        if (columnConfigsMatch(currentConfig, normalizedConfig)) return;
        editor.updateColumnConfig(tableElement.dndId, normalizedConfig);
    };

    const handleToggle = (typeId: string, checked: boolean) => {
        if (checked) {
            const existing = currentConfig ?? [];
            const next: UIArTableColumnOverride[] = [...existing, { typeId }];
            dispatchConfig(next);
        } else {
            if (!currentConfig) return;
            const next = currentConfig.filter(c => c.typeId !== typeId);
            dispatchConfig(next.length > 0 ? next : undefined);
        }
    };

    const handleLabelChange = (typeId: string, value: string) => {
        if (!currentConfig) return;
        const next = currentConfig.map(c =>
            c.typeId === typeId
                ? { ...c, labelOverride: value || undefined }
                : c
        );
        dispatchConfig(next);
    };

    const handleAddAll = () => {
        const next: UIArTableColumnOverride[] = tableType.columns.map(col => {
            const existing = currentConfig?.find(c => c.typeId === col.typeId);
            return { typeId: col.typeId, ...(existing?.labelOverride ? { labelOverride: existing.labelOverride } : {}) };
        });
        dispatchConfig(next);
    };

    const handleRemoveAll = () => {
        dispatchConfig(undefined);
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(String(event.active.id));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over || active.id === over.id || !currentConfig) return;

        const includedTypeIds = rows.filter(r => r.included).map(r => r.typeId);
        const oldIndex = includedTypeIds.indexOf(String(active.id));
        const newIndex = includedTypeIds.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(includedTypeIds, oldIndex, newIndex);
        const configMap = new Map(currentConfig.map(c => [c.typeId, c]));
        const next: UIArTableColumnOverride[] = reordered
            .map(id => configMap.get(id))
            .filter((c): c is UIArTableColumnOverride => c !== undefined);
        dispatchConfig(next);
    };

    const includedIds = useMemo(
        () => rows.filter(r => r.included).map(r => r.typeId),
        [rows]
    );
    const includedIdSet = useMemo(
        () => new Set(includedIds),
        [includedIds]
    );

    const tableColumns = [
        {
            title: '',
            key: 'checkbox',
            width: 40,
            render: (_: unknown, record: ColumnRow) => (
                <Checkbox
                    checked={record.included}
                    onChange={(e) => handleToggle(record.typeId, e.target.checked)}
                    aria-label={`Include ${record.label} in custom layout`}
                />
            ),
        },
        {
            title: 'Column',
            key: 'label',
            render: (_: unknown, record: ColumnRow) => (
                <Text type={record.included ? undefined : 'secondary'}>{record.label}</Text>
            ),
        },
        {
            title: 'Label Override',
            key: 'labelOverride',
            width: 200,
            render: (_: unknown, record: ColumnRow) => (
                <Input
                    size="small"
                    placeholder={record.label}
                    value={record.labelOverride}
                    onChange={(e) => handleLabelChange(record.typeId, e.target.value)}
                    disabled={!record.included}
                    aria-label={`Custom label override for ${record.label}`}
                />
            ),
        },
    ];

    return (
        <FlexCol stretch>
            <FlexBox stretch>
                <Button 
                    onClick={handleAddAll} 
                    aria-label="Include all columns in custom layout"
                >
                    Select All (Custom)
                </Button>
                <Button 
                    onClick={handleRemoveAll} 
                    aria-label="Use default column visibility"
                >
                    Use Default (Show All)
                </Button>
            </FlexBox>
            <Text type="secondary">
                {currentConfig
                    ? `${currentConfig.length} of ${tableType.columns.length} columns in custom layout`
                    : `Default mode: all ${tableType.columns.length} columns visible`}
            </Text>
            <Text type="secondary">
                {currentConfig
                    ? 'Checked columns define the custom visibility and order.'
                    : 'Check columns to start a custom layout that controls visibility and order.'}
            </Text>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={includedIds}
                    strategy={verticalListSortingStrategy}
                >
                    <Table
                        dataSource={rows}
                        columns={tableColumns}
                        rowKey="typeId"
                        pagination={false}
                        size="small"
                        style={{ width: '100%' }}
                        components={{
                            header: {
                                row: (props: any) => (
                                    <tr {...props}>
                                        <th style={{ width: 40 }} />
                                        {props.children}
                                    </tr>
                                ),
                            },
                            body: {
                                row: (props: any) => {
                                    const rowKey = props['data-row-key'] as string;
                                    const isIncluded = includedIdSet.has(rowKey);
                                    if (isIncluded) {
                                        return (
                                            <SortableRow id={rowKey} {...props}>
                                                {props.children}
                                            </SortableRow>
                                        );
                                    }
                                    return (
                                        <tr {...props}>
                                            <td style={{ width: 40 }} />
                                            {props.children}
                                        </tr>
                                    );
                                },
                            },
                        }}
                    />
                </SortableContext>
                <DragOverlay>
                    {activeId ? (
                        <div style={{
                            opacity: 0.75,
                            padding: `${token.paddingXS}px ${token.paddingSM}px`,
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            boxShadow: token.boxShadowSecondary,
                        }}>
                            <Text>{rows.find(r => r.typeId === activeId)?.label ?? activeId}</Text>
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </FlexCol>
    );
};

export default ColumnConfigEditor;
